<template>
  <div v-if="survey && summary"
       :class="['survey-response', { pdf: isPdf }]">
    <div class="pdf-meta">
      <div class="title">
        {{ $t('userResearch.survey.response.surveySummary') }}
      </div>
      <p>
        {{ $t('userResearch.survey.response.response') }}
        <span class="number">
            {{ summary.totalAnswered | thousand }}
          </span>
        {{ $t('userResearch.survey.response.responseUnit') }}
      </p>
    </div>
    <div class="survey-meta">
      <div class="duration">
        <div v-if="isStarted"
             class="now-date"
             @click.stop="toggleRangePicker">
          <div class="date-picker">
            <div class="periods-label">
              <span class="timezone">{{timezoneName}}</span>{{ dateString }}
              <i class="icon icon--calendar-green"></i>
            </div>
            <transition name="calendar">
              <date-range-picker v-if="showDateRangePicker"
                                 :dateRange="dateRange"
                                 :maxDays="184"
                                 :minDate="surveyDateRange.startDate"
                                 :maxDate="computedMaxDate"
                                 :timezone="timezone"
                                 :timezoneName="timezoneName"
                                 :showPaid="true"
                                 @onRangeCancel="showDateRangePicker = false"
                                 @onRangeChange="onRangeChange" />
            </transition>
          </div>
        </div>
      </div>
      <h2 class="name">
        {{ survey.surveyName }}
      </h2>
    </div>
    <div v-if="isStarted"
         class="response-meta">
      <div class="count">
        {{ $t('userResearch.survey.response.response') }}
        <span class="number">
          {{ summary.totalAnswered | thousand }}
        </span>
        {{ $t('userResearch.survey.response.responseUnit') }}
      </div>
      <div class="response-tab"
           :class="{ individual: show === 'individual' }">
        <button :class="{ on: show === 'summary' }"
                @click="changeShowing('summary')">
          {{ $t('userResearch.survey.response.summary') }}
        </button>
        <button :class="{ on: show === 'individual' }"
                @click="changeShowing('individual')">
          {{ $t('userResearch.survey.response.individual') }}
        </button>
      </div>
      <div class="buttons">
        <template v-if="showing === 'summary'">
          <button @click="downloadExcel">
            <i class="icon icon--excel green"></i>
          </button>
          <button @click="confirmPdf">
            <i class="icon icon--print"></i>
          </button>
        </template>
        <template v-if="showing === 'individual'">
          <button @click="downloadExcelAll">
            <i class="icon icon--excel green"></i>
          </button>
        </template>
      </div>
    </div>
    <div v-else
         class="response-no">
      <p v-html="$t('userResearch.survey.response.notStarted')"></p>
    </div>

    <div v-if="isStarted"
         class="response-body">
      <transition name="fade">
        <div v-if="loading"
             class="loader">
          <ba-loading :applyBackground="true"
                      :showLoadingText="true"/>
        </div>
      </transition>

      <transition name="fade">
        <div v-if="makingFile"
             id="loader-pdf"
             class="loader-pdf">
          <ba-loading :applyBackground="true"
                      :showLoadingText="true"/>
        </div>
      </transition>

      <transition name="fade">
        <response-summary v-if="showing === 'summary'"
                          v-show="!loading"
                          class="body-wrap"
                          :survey="survey"
                          :summary="summary"
                          :startDate="startDate"
                          :endDate="endDate"
                          :isPdf="isPdf"
                          @chartString="chartString = $event"
                          @loaded="loading = false" />
      </transition>

      <transition name="fade">
        <response-individual v-if="showing === 'individual'"
                             v-show="!loading"
                             class="body-wrap"
                             :survey="survey"
                             :summary="summary"
                             :startDate="startDate"
                             :endDate="endDate"
                             :isPdf="isPdf"
                             @loaded="loading = false" />
      </transition>

      <dimmed v-if="pdfAlert">
        <alert :title="$t('userResearch.survey.response.confirmPdf', { count: SURVEY_MAX_TEXT_ANSWER_COUNT })"
               :submit="$t('common.yes')"
               @onSubmit="downloadPdf" />
      </dimmed>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { parse, format, startOfDay } from 'date-fns'
import { SURVEY_CONSTANT, SURVEY_MAX_TEXT_ANSWER_COUNT } from '@/libs/constants'

import Excel from '@/libs/excel-export'
import { EventBus } from '@/libs/event-bus'
import { getResponseSummary, getAllAnswer } from '@/api/survey'

import ResponseSummary from '@/components/user-research/survey/ResponseSummary'
import ResponseIndividual from '@/components/user-research/survey/ResponseIndividual'

import DateRangePicker from '@/components/ui/ba/DateRangePicker'
import BaLoading from '@/components/ui/ba/Loading'
import Dimmed from '@/components/ui/Dimmed'
import Alert from '@/components/ui/Alert'
import { getOffset, utcToLocalTimezone } from '@/libs/utils'
import { isInIframe } from '@/libs/auth-helper'

export default {
  name: 'SurveyResponse',
  components: {
    ResponseSummary,
    ResponseIndividual,
    DateRangePicker,
    BaLoading,
    Dimmed,
    Alert
  },
  created () {
    this.load().then(result => {
      this.summary = result
      this.loading = false
      this.$emit('loaded', true)
    })
  },
  methods: {
    load () {
      return getResponseSummary({
        sid: this.domain.orgSid || this.domain.sid,
        surveyId: this.survey.surveyId,
        start: this.startDate,
        end: this.endDate
      })
    },
    changeShowing (showing) {
      this.loading = true
      this.show = showing
      setTimeout(() => {
        this.showing = showing
      }, 300)
    },
    toggleRangePicker () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeReferrerList')
      EventBus.$emit('closeReportPageList')
      EventBus.$emit('closeJourneyPageList')
      this.showDateRangePicker = !this.showDateRangePicker
    },
    onRangeChange (data) {
      const range = data.range
      this.$emit('changeStart', format(range.startDate, 'YYYY-MM-DD'))
      this.$emit('changeEnd', format(range.endDate, 'YYYY-MM-DD'))

      this.loading = true
      setTimeout(() => {
        this.load().then(result => {
          this.summary = result
          this.loading = false
        })
      }, 100)

      this.toggleRangePicker()
    },
    downloadExcelAll () {
      this.$emit('onDownload', true)
      let excel = new Excel(this)
      this.makingFile = true
      getAllAnswer({
        sid: this.domain.orgSid || this.domain.sid,
        surveyId: this.survey.surveyId,
        start: this.startDate,
        end: this.endDate
      }).then(result => {
        excel.setSurveyAnswerData(this.survey.questions, result, this.dateString, this.timezone, this.timezoneName).then(() => {
          excel.download(`${this.domain.domain}_${this.survey.surveyName}_${this.$t('userResearch.survey.excel.allAnswer')}`)
          this.makingFile = false
        })
      }).catch((error) => {
        console.error(error)
        this.makingFile = false
      })
    },
    downloadExcel () {
      this.$emit('onDownload', true)
      let excel = new Excel(this)
      this.makingFile = true
      excel.setSurveySummaryData(this.summary.countMap, this.dateString, this.timezone, this.timezoneName).then(() => {
        excel.download(`${this.domain.domain}_${this.survey.surveyName}`)
        this.makingFile = false
      })
    },
    confirmPdf () {
      let hasText = false
      if (this.summary.totalAnswered > SURVEY_MAX_TEXT_ANSWER_COUNT) {
        for (const question of Object.values(this.summary.statsPerQuestion)) {
          if (question.qustionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
            hasText = true
            break
          }
        }
      }
      if (hasText) {
        this.pdfAlert = true
      } else {
        this.downloadPdf()
      }
    },
    downloadPdf () {
      this.pdfAlert = false
      this.showDateRangePicker = false
      let routeData = this.$router.resolve({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          start: this.startDate,
          end: this.endDate,
          pdf: 1,
          chart: this.chartString
        }
      })
      window.open(routeData.href, isInIframe() ? '_self' : '_blank')
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    timezone () {
      return getOffset(this.timezoneName)
    },
    dateString () {
      return format(parse(this.startDate), 'YYYY-MM-DD') + ' ~ ' + format(parse(this.endDate), 'YYYY-MM-DD')
    },
    dateRange () {
      return {
        startDate: parse(this.startDate),
        endDate: parse(this.endDate)
      }
    },
    surveyDateRange () {
      return {
        startDate: parse(this.survey.startDate),
        endDate: parse(this.survey.endDate)
      }
    },
    computedMaxDate () {
      return parse(Math.min(startOfDay(this.surveyDateRange.endDate), startOfDay(utcToLocalTimezone(new Date(), this.domain.timezoneName))))
    },
    isStarted () {
      return utcToLocalTimezone(new Date(), this.domain.timezoneName) >= utcToLocalTimezone(parse(this.survey.startDate), this.domain.timezoneName)
    },
    SURVEY_MAX_TEXT_ANSWER_COUNT () {
      return SURVEY_MAX_TEXT_ANSWER_COUNT
    }
  },
  props: [
    'domain',
    'survey',
    'startDate',
    'endDate',
    'isPdf',
    'timezoneName'
  ],
  data () {
    return {
      loading: true,
      show: 'summary',
      showing: 'summary',
      summary: null,
      showDateRangePicker: false,
      makingFile: false,
      pdfAlert: false,
      chartString: ''
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-response {
  width: 970px;
  margin: 0 auto;
  padding-bottom: 200px;

  &.pdf {
    width: auto;
    background-color: #F4F4F4;
    padding: 40px !important;

    .pdf-meta {
      display: block;
    }

    .duration {
      .icon--calendar-green {
        display: none;
      }
    }

    .response-meta {
      display: none;
    }

    & /deep/ {
      .other {
        button {
          display: none;
        }
      }

      .chart-type {
        display: none;
      }

      .summary-item {
        .item {
          box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.08);
          margin: 12px -1px 19px;
        }
      }

      .question-answers {
        padding-top: 20px;

        .ui-navigation {
          display: none;
        }
      }
    }
  }

  .loader {
    position: fixed;
    z-index: 100;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &.fade-enter-active, &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &.fade-enter, &.fade-leave-to{
      opacity: 0;
    }
  }

  .loader-pdf {
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    background: #f4f4f1;

    &.fade-enter-active, &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &.fade-enter, &.fade-leave-to{
      opacity: 0;
    }
  }

  .body-wrap {
    &.fade-enter-active, &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &.fade-enter, &.fade-leave-to{
      opacity: 0;
    }
  }

  .pdf-meta {
    display: none;
    margin: -8px 0 30px;

    .title {
      display: inline-block;
      vertical-align: middle;
      font-size: 24px;
      font-weight: 500;
      color: #444;
    }

    p {
      display: inline-block;
      vertical-align: middle;
      height: 17px;
      line-height: 17px;
      font-size: 18px;
      font-weight: 500;
      color: #2f2f2f;
      border-left: 1px solid #cccccc;
      margin-left: 20px;
      padding-left: 20px;

      .number {
        font-size: 18px;
        font-weight: 500;
        color: #ec0047;
        margin-left: 5px;
      }
    }
  }

  .survey-meta {
    text-align: center;
    border-bottom: 1px solid #d7d7d7;
    padding-bottom: 40px;
    margin-bottom: 30px;

    .duration {
      padding: 60px 0 12px;
      font-size: 16px;
      color: #767676;

      .date-picker {
        margin: 0 0 0 8px;
        position: relative;
        border-radius: 8px;
        vertical-align: middle;
        display: inline-block;
        width: calc(100% - 5px);

        .periods-label {
          cursor: pointer;
          font-weight: 600;
          font-size: 16px;
          color: #0e90a5;
          margin: -8px 0;
          line-height: 1.25;
          transition: color 0.2s ease;

          & .timezone {
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: #767676;
            vertical-align: middle;

            &:after {
              content: "";
              display: inline-block;
              vertical-align: middle;
              width: 1px;
              height: 12px;
              background-color: #ccc;
              margin: 0 15px;
            }
          }
        }

        .icon--calendar-green {
          margin-top: -3px;
          vertical-align: middle;
          pointer-events: none;
          padding: 2px;
          border-radius: 3px;
          border: solid 1px transparent;
          background-size: 16px 16px;
          transition: border-color 0.2s ease;
        }

        &:hover {
          .periods-label {
            color: #035e6d;
          }

          .icon--calendar-green {
            border-color: #d9d9d9;
          }
        }

        .date_range_picker {
          top: 15px;
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }

    .name {
      font-size: 32px;
      font-weight: 500;
      line-height: 1.31;
      color: #2f2f2f;
      padding-bottom: 10px;
    }
  }

  .response-meta {
    text-align: center;
    position: relative;

    .count {
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      font-size: 16px;
      font-weight: 500;
      color: #2f2f2f;

      .number {
        font-size: 20px;
        color: #ec0047;
        margin-left: 3px;
      }
    }

    .buttons {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);

      button {
        width: 34px;
        height: 34px;
        padding: 8px;
        margin-left: 5px;
        border-radius: 20px;
        border: solid 1px #bbb;
        background-color: #ffffff;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: #ebebeb;
        }

        .icon {
          width: 16px;
          height: 16px;
        }
      }
    }

    .response-tab {
      display: inline-block;
      width: 250px;
      border: 1px solid #e0e0e0;
      background-color: #ffffff;
      transition: background-color 0.2s cubic-bezier(0, 0, 0, 1), border-color 0.2s cubic-bezier(0, 0, 0, 1);
      border-radius: 20px;
      position: relative;

      button {
        width: 125px;
        text-align: center;
        line-height: 36px;
        height: 36px;
        color: #888888;
        position: relative;
        font-size: 15px;
        z-index: 2;
        transition: color 0.2s cubic-bezier(0, 0, 0, 1);

        &:first-child {
          box-sizing: border-box;
          padding-left: 5px;
        }

        &:last-child {
          box-sizing: border-box;
        }

        &.on {
          color: #ffffff;
        }
      }

      &::before {
        content: "";
        position: absolute;
        z-index: 1;
        top: -1px;
        left: -1px;
        background: #ec0147;
        border-radius: 20px;
        transition: left 0.2s cubic-bezier(0, 0, 0, 1);
        display: block;
        width: 130px;
        height: 38px;
        box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
      }

      &.individual::before {
        left: 120px;
      }
    }
  }

  .response-no {
    position: relative;
    height: calc(100vh - 470px);
    min-height: 200px;
    margin-bottom: -150px;

    p {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 20px;
      color: #444;
    }
  }

  .calendar-enter-active,
  .calendar-leave-active {
    transform-origin: top;
    transition: transform 0.3s ease,
                opacity 0.3s ease,
                height 0.3s ease !important;
  }

  .calendar-enter,
  .calendar-leave-to{
    transform: translate(-50%, -30px) scaleY(0.5) !important;
    opacity: 0;
  }
}
</style>
